import useHackathon from '@/data/hook/hackathon/useHackathon';
import useWinners from '@/data/hook/hackathon/useWinners';
import useManualQuery from '@/data/hook/query/v1/useManualQuery';
import { winnersQueryKeys } from '@/data/services/querykeys';
import {
  createWinner,
  deleteWinner,
  updateWinner,
} from '@/data/services/winnerServices';
import { Winner, WinnerCreate } from '@/models/Hackathon';
import { Project } from '@/models/Project';
import { getErrorMessage } from '@/utils/getErrorMessage';
import {
  getWinnerByProjectId,
  getWinnerByRank,
  getWinnersCount,
} from '@/utils/hackathon';
import { getCourseFlag } from '@/utils/project';
import alert from '@/utils/shared/alert';
import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type HackathonWinnersProps = {
  project: Project;
  onMutate?: (project: Project) => void;
};

type Stage = {
  payload: Partial<WinnerCreate>;
  winnerShow: Winner;
  winnerUpdate: Winner;
};
export default function useSetWinners({ project }: HackathonWinnersProps) {
  const { t: tError } = useTranslation('translation', {
    keyPrefix: 'projects.error',
  });
  const [isCommittingStage, setCommittingStage] = useState(false);

  const [inStage, setInStage] = useState(false);

  const stage = useRef<Stage>();

  const { hackathon, isLoading: isLoadingHackathon } = useHackathon();

  const {
    winners,
    winnersByCategory,
    isLoading: isLoadingWinners,
  } = useWinners(hackathon);

  const {
    updateItem: updateWinners,
    addItem: addWinner,
    removeItem: removeWinner,
  } = useManualQuery(
    winnersQueryKeys.list({
      hackathonId: hackathon?.id,
      pageSize: getWinnersCount(),
    }),
  );

  const handleCreateWinner = async (rank: number) => {
    if (!hackathon) return;
    const courseFlag = getCourseFlag(project.flags);
    if (!courseFlag) {
      alert.error(tError('notHaveCourseFlag'));
      return;
    }
    const result = await createWinner({
      category: courseFlag.name,
      rank,
      hackathonId: hackathon.id,
      projectId: project.id,
    });

    addWinner(result);

    return result;
  };

  const handleUpdateWinner = async (
    payload: Partial<WinnerCreate>,
    winner: Winner,
  ) => {
    const result = await updateWinner(winner.id, payload);
    updateWinners(result);
    return result;
  };

  const commitStage = async () => {
    if (!stage.current) return;
    setCommittingStage(true);
    const { payload, winnerUpdate, winnerShow } = stage.current;
    if (winnerUpdate.id !== winnerShow.id) {
      await deleteWinner(winnerShow.id);
      removeWinner(winnerShow.id);
    }
    const result = await handleUpdateWinner(payload, winnerUpdate);
    updateWinners(result);
    setInStage(false);
    setCommittingStage(false);
  };

  const prepareStage = (stageData: Stage) => {
    stage.current = stageData;
    setInStage(true);
  };

  const setupWinner = (rank: number) => {
    const courseFlag = getCourseFlag(project.flags);
    if (!courseFlag) return {};
    const winnerRank = getWinnerByRank(
      rank,
      winnersByCategory[courseFlag.name],
    );
    const winnerProjectId = getWinnerByProjectId(project.id, winners);
    return {
      winnerRank,
      winnerProjectId,
    };
  };

  const setProjectWinnerAndDeleteOldWinner = (
    rank: number,
    winner: Winner,
    oldWinner: Winner,
  ) => {
    prepareStage({
      payload: { rank },
      winnerUpdate: winner,
      winnerShow: oldWinner,
    });
  };

  const changeWinnerRank = (rank: number, winner: Winner) => {
    return handleUpdateWinner({ rank }, winner);
  };

  const changeWinnerProject = (winner: Winner) => {
    return prepareStage({
      payload: { projectId: project.id },
      winnerShow: winner,
      winnerUpdate: winner,
    });
  };

  const handleWinner = async (rank: number) => {
    const { winnerRank, winnerProjectId } = setupWinner(rank);

    if (winnerProjectId && winnerRank)
      return setProjectWinnerAndDeleteOldWinner(
        rank,
        winnerProjectId,
        winnerRank,
      );

    if (winnerProjectId) return changeWinnerRank(rank, winnerProjectId);

    if (winnerRank) return changeWinnerProject(winnerRank);

    return handleCreateWinner(rank);
  };

  const { mutate: setWinner, isLoading: isSetting } = useMutation(
    handleWinner,
    {
      onError: e => alert.error(getErrorMessage(e)),
    },
  );

  const clearStage = () => {
    stage.current = undefined;
    setInStage(false);
  };

  return {
    setWinner,
    isLoading: isLoadingWinners || isLoadingHackathon || isSetting,
    inStage,
    stage,
    isCommittingStage,
    commitStage,
    clearStage,
  };
}
